import React from 'react';

import { ErrorPage } from '../../../components/error_page';
import { LoadingIndicator } from '../../../components/loading_indicator';
import { UserRoleInCompany } from '../../../lib/graphql';
import { CompanyUsersHeader } from '../../components/company_users_header';
import { CompanyUsersList } from '../../components/company_users_list';
import { useUserSearch } from '../../hooks';
import { useCompanyUsersMutations } from '../company_users_mutations';
import { useViewerCompany } from '../hooks';

import * as styles from './styles.css';

interface CompanyUsersProps {
  hideHeader?: boolean;
  searchQuery?: string | undefined;
  onSearchChange?: (query: string | undefined) => void;
}

export const CompanyUsers = ({
  hideHeader = false,
  searchQuery: externalSearchQuery,
  onSearchChange: externalOnSearchChange,
}: CompanyUsersProps = {}) => {
  const [internalSearchQuery, setInternalSearchQuery] = React.useState<string | undefined>(undefined);

  // Use external search state if provided, otherwise use internal state
  const searchQuery = externalSearchQuery !== undefined ? externalSearchQuery : internalSearchQuery;
  const setSearchQuery = externalOnSearchChange ?? setInternalSearchQuery;

  const searchData = useUserSearch(searchQuery);
  const mutations = useCompanyUsersMutations();
  const { userInCompany, permissions } = useViewerCompany();

  const handleSearchChange = React.useCallback((query: string | undefined) => setSearchQuery(query), [setSearchQuery]);

  const isAllowedToAddOrRemoveUsers = React.useMemo(
    () =>
      userInCompany?.role === UserRoleInCompany.ADMIN &&
      !searchData.isPresentOnTpPlatform &&
      permissions.hasUserManagementEnabled,
    [userInCompany?.role, searchData.isPresentOnTpPlatform, permissions.hasUserManagementEnabled],
  );

  const shouldShowUsersList = React.useMemo(
    () => searchData.displayData.users !== undefined,
    [searchData.displayData.users],
  );

  const isViewingUserAdmin = React.useMemo(
    () => userInCompany?.role === UserRoleInCompany.ADMIN,
    [userInCompany?.role],
  );

  // Handle initial loading error
  if (!searchData.hasEverLoadedData && !searchData.isLoading) {
    return <ErrorPage type="not-found" />;
  }

  return (
    <>
      {!hideHeader && (
        <CompanyUsersHeader
          isLoading={searchData.isLoading}
          addUser={mutations.addUser}
          isAllowedToAddUsers={isAllowedToAddOrRemoveUsers}
          count={searchData.countResult}
          searchQuery={searchQuery}
          onSearchChange={handleSearchChange}
        />
      )}

      <div className={styles.container}>
        {shouldShowUsersList ? (
          <CompanyUsersList
            users={searchData.displayData.users}
            hasNextPage={searchData.displayData.hasNextPage}
            loadNextPage={searchData.displayData.loadNextPage}
            removeUserFromCompany={mutations.removeUser}
            changeUserToCompanyAdmin={mutations.makeAdmin}
            isViewingUserAdmin={isViewingUserAdmin}
            isAllowedToAddOrRemoveUsers={isAllowedToAddOrRemoveUsers}
            onClearSearch={() => handleSearchChange(undefined)}
          />
        ) : searchData.shouldShowInitialLoading ? (
          <LoadingIndicator />
        ) : null}
      </div>
    </>
  );
};
